import React, { useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Alert } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import CountrySelector from '../../components/ui/CountrySelector';
import logo from '../../assets/logo.png';
import '../../fonts.css';
import backgroundImage from '../../assets/background.jpg';
import { API_BASE_URL } from '../../config/api';;

const Signup = () => {
  const emailRef = useRef();
  const passwordRef = useRef();
  const passwordConfirmRef = useRef();
  const displayNameRef = useRef();
  
  // Add state for country
  const [country, setCountry] = useState('');
  
  // Make sure to import login from useAuth
  const { signup, login, signInWithGoogle, signInWithFacebook, getIdToken, logout } = useAuth();
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
  
    if (passwordRef.current.value !== passwordConfirmRef.current.value) {
      return setError('Passwords do not match');
    }
    
    if (!country) {
      return setError('Please select your country');
    }
  
    try {
      setError('');
      setLoading(true);
  
      // Create user in Firebase Auth first
      console.log('Creating user in Firebase Auth...');
      const userCredential = await signup(emailRef.current.value, passwordRef.current.value);
      
      // User is now logged in, get their token
      console.log('Getting user token...');
      const token = await userCredential.user.getIdToken();
      
      // Register the user in your backend using the Firebase token
      console.log('Registering user in backend...');
      const backendResponse = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: emailRef.current.value,
          displayName: displayNameRef.current.value,
          password: passwordRef.current.value,
          country: country, // Add country to registration data
          initialCredits: 5 // Ensure new users get 5 credits
        }),
      });
      
      const responseData = await backendResponse.json();
      if (!backendResponse.ok) {
        console.error('Backend registration error', responseData);
        throw new Error(responseData.error || 'Failed to register with backend');
      }
      
      console.log('User registered successfully', responseData);
      
      // Successfully created account and logged in
      navigate('/');
    } catch (error) {
      console.error('Registration error:', error);
      // Handle Firebase Auth errors
      if (error.code === 'auth/email-already-in-use') {
        setError('Account already exists! Please login');
      } else {
        setError('Failed to create an account: ' + (error.message || error));
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignIn() {
    try {
      setError('');
      
      // Verify country is selected
      if (!country) {
        setError('Please select your country before signing in with Google');
        return;
      }
      
      setLoading(true);
      
      console.log('Signing in with Google...');
      // First sign in with Google via Firebase
      const userCredential = await signInWithGoogle();
      
      // Get the user information
      const user = userCredential.user;
      console.log('Getting user token...');
      const token = await user.getIdToken();
      
      // Verify the token with backend, indicating this is from signup page
      console.log('Verifying token...');
      const verifyResponse = await fetch(`${API_BASE_URL}/auth/verify-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          idToken: token,
          sourcePage: 'signup',
          country: country // Include country information
        }),
      });
      
      if (!verifyResponse.ok) {
        const errorData = await verifyResponse.json();
        throw new Error(errorData.error || 'Failed to verify user');
      }
      
      // Register the user in your backend
      console.log('Creating user document...');
      const backendResponse = await fetch(`${API_BASE_URL}/auth/create-user-doc`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: user.email,
          displayName: user.displayName || '',
          provider: 'google',
          user_id: user.uid,
          country: country, // Add country here
          initialCredits: 5 // Ensure new users get 5 credits
        }),
      });
      
      if (!backendResponse.ok) {
        const responseData = await backendResponse.json();
        console.error('Failed to register Google user in backend', responseData);
        throw new Error(responseData.error || 'Failed to create user document');
      }
      
      console.log('Google sign-in complete');
      navigate('/');
    } catch (error) {
      console.error('Google sign-in error:', error);
      setError('Failed to sign in with Google: ' + error.message);
      
      // If we got this far but something failed, sign out to clean up
      try {
        await logout();
      } catch (logoutError) {
        console.error('Logout error:', logoutError);
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleFacebookSignIn() {
    try {
      setError('');
      
      // Verify country is selected
      if (!country) {
        setError('Please select your country before signing in with Facebook');
        return;
      }
      
      setLoading(true);
      
      // First sign in with Facebook via Firebase
      const userCredential = await signInWithFacebook();
      
      // Get the user information
      const user = userCredential.user;
      const token = await user.getIdToken();
      
      // Verify the token with backend, indicating this is from signup page
      const verifyResponse = await fetch(`${API_BASE_URL}/auth/verify-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          idToken: token,
          sourcePage: 'signup',
          country: country // Include country information
        }),
      });
      
      if (!verifyResponse.ok) {
        const errorData = await verifyResponse.json();
        throw new Error(errorData.error || 'Failed to verify user');
      }
      
      // Register the user in your backend
      const backendResponse = await fetch(`${API_BASE_URL}/auth/create-user-doc`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: user.email,
          displayName: user.displayName || '',
          provider: 'facebook',
          user_id: user.uid,
          country: country,
          initialCredits: 5
        }),
      });
      
      if (!backendResponse.ok) {
        const responseData = await backendResponse.json();
        console.error('Failed to register Facebook user in backend', responseData);
        throw new Error(responseData.error || 'Failed to create user document');
      }
      
      navigate('/');
    } catch (error) {
      setError('Failed to sign in with Facebook: ' + error.message);
      
      // If we got this far but something failed, sign out to clean up
      try {
        await logout();
      } catch (logoutError) {
        console.error('Logout error:', logoutError);
      }
    } finally {
      setLoading(false);
    }
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      {/* Background image with opacity */}
      <div 
        className="absolute inset-0 z-0" 
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.2, // 20% opacity
        }}
      ></div>
      <div className="relative z-10 max-w-md w-full space-y-8 bg-white bg-opacity-10 p-8 rounded-lg shadow-lg">
        <div>
          <div className="flex justify-center mb-4">
            <img 
              src={logo}
              alt="Flokraft Logo" 
              className="h-24 w-auto"
            />
          </div>
        
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 metalord-font">
            AI Match Analysis
          </h2>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Join Flokraft AI Match Analysis
          </p>
          <p className="mt-1 text-center text-sm font-semibold text-blue-600">
            Get 5 free credits when you sign up!
          </p>
        </div>
        
        {error && <Alert variant="destructive">{error}</Alert>}
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="display-name" className="sr-only">
                Display Name
              </label>
              <input
                id="display-name"
                name="name"
                type="text"
                ref={displayNameRef}
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Display Name"
              />
            </div>
            <div>
              <label htmlFor="email-address" className="sr-only">
                Email address
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                ref={emailRef}
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                ref={passwordRef}
                autoComplete="new-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Password"
              />
            </div>
            <div>
              <label htmlFor="password-confirm" className="sr-only">
                Confirm Password
              </label>
              <input
                id="password-confirm"
                name="password-confirm"
                type="password"
                ref={passwordConfirmRef}
                autoComplete="new-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Confirm Password"
              />
            </div>
            {/* Add Country Selector */}
            <div>
              <label htmlFor="country" className="sr-only">
                Country
              </label>
              <CountrySelector
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                placeholder="Select Country (Required)"
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                required={true}
              />
            </div>
          </div>

          <div>
            <Button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {loading ? 'Creating Account...' : 'Sign Up'}
            </Button>
          </div>
          
          <div className="flex items-center justify-center">
            <span className="text-gray-500 text-sm">or sign up with</span>
          </div>
          
          <div className="grid gap-3">
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  d="M12.545 10.239v3.821h5.445c-0.712 2.315-2.647 3.972-5.445 3.972-3.332 0-6.033-2.701-6.033-6.032s2.701-6.032 6.033-6.032c1.498 0 2.866 0.549 3.921 1.453l2.814-2.814c-1.79-1.677-4.184-2.702-6.735-2.702-5.514 0-10 4.486-10 10s4.486 10 10 10c5.514 0 10-4.486 10-10 0-0.837-0.12-1.64-0.329-2.411h-9.671z"
                  fill="#4285F4"
                />
              </svg>
              <span className="ml-2">Google</span>
            </button>
            
            
          </div>
        </form>
        
        <div className="text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Signup;